import { _isNil, _notNil } from '@/littledash';
import type { ChangeEventHandler, CSSProperties, RefObject } from 'react';
import { useCallback, useRef, useState } from 'react';
import type {
  DataTableCellIndexCoordinate,
  DataTableCellSelectionState,
  DataTableCellState,
  DataTableDirection,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DateInputUtils, DateUtils } from '@/utils/Date.utils';
import { useDataTable } from '../DataTableProvider';
import { useCellCoordinate } from './useCellCoordinate';
import { useCellData } from './useCellData';
import { useCellMouseHandlers } from './useCellMouseHandlers';
import { useCellPositioning } from './useCellPositioning';
import { useCellSelectionState } from './useCellSelectionState';
import { useCellState } from './useCellState';
import { useGlobalEventListenerAbort } from './useGlobalEventListenerAbort';

/**
 * Display format options for picker inputs
 *
 * Defines the precision and format of datetime values displayed in the picker.
 * Maps to HTML5 input types and determines conversion behavior.
 */
export type PickerDisplayFormat = 'date' | 'datetime-hm' | 'datetime-hms';

/**
 * Picker display format constants
 *
 * @example
 * // For timestamp cells with full precision
 * displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds
 *
 * @example
 * // For collect cells with minute precision
 * displayFormat: PickerDisplayFormat.DateTimeHoursMinutes
 */
export const PickerDisplayFormat = {
  /** Date only: 2024-02-15 (future use) */
  Date: 'date',
  /** DateTime with hours and minutes: 2024-02-15T10:30 */
  DateTimeHoursMinutes: 'datetime-hm',
  /** DateTime with hours, minutes, and seconds: 2024-02-15T10:30:45 */
  DateTimeHoursMinutesSeconds: 'datetime-hms',
} as const satisfies Record<string, PickerDisplayFormat>;

/**
 * Configuration for picker cell behavior
 *
 * @property coordinate - Cell position in the data table (index-based)
 * @property positionOffset - Pixel offset for positioning the picker input overlay
 * @property displayFormat - Controls picker precision (HH:MM vs HH:MM:SS)
 */
interface UsePickerCellBehaviorConfig {
  readonly coordinate: DataTableCellIndexCoordinate;
  readonly positionOffset: Readonly<{ x: number; y: number }>;
  readonly displayFormat: PickerDisplayFormat;
}

/**
 * Cell data and state information
 */
export interface PickerCellState {
  readonly value: string | null | undefined;
  readonly state: DataTableCellState;
  readonly selectionState: DataTableCellSelectionState;
  readonly hasGroupBorder: boolean;
  readonly tooltip: string | undefined;
}

/**
 * Picker control and state
 */
export interface PickerControl {
  readonly active: boolean;
  readonly inputRef: RefObject<HTMLInputElement>;
  readonly positionStyle: CSSProperties;
  readonly handleInputChange: ChangeEventHandler<HTMLInputElement>;
  readonly activate: () => void;
}

/**
 * Mouse interaction handlers for cell selection
 */
export interface PickerMouseHandlers {
  readonly onMouseDown: React.MouseEventHandler<HTMLDivElement>;
  readonly onMouseEnter: React.MouseEventHandler<HTMLDivElement>;
  readonly onMouseUp: React.MouseEventHandler<HTMLDivElement>;
}

/**
 * Return value from usePickerCellBehavior hook
 *
 * Provides all state, handlers, and refs needed to render a picker-enabled cell.
 * Properties are grouped into logical categories for better organization:
 * - `cell`: Cell data and visual state
 * - `picker`: Picker control and input handling
 * - `mouseHandlers`: Mouse interaction handlers for selection
 *
 * @example
 * const { cell, picker, mouseHandlers } = usePickerCellBehavior(config);
 *
 * return (
 *   <div
 *     onMouseDown={mouseHandlers.onMouseDown}
 *     style={picker.positionStyle}
 *   >
 *     {cell.value || 'Add Timestamp'}
 *     <input ref={picker.inputRef} onChange={picker.handleInputChange} />
 *   </div>
 * );
 */
export interface UsePickerCellBehaviorReturn {
  readonly cell: PickerCellState;
  readonly picker: PickerControl;
  readonly mouseHandlers: PickerMouseHandlers;
}

/**
 * Hook for datetime picker cell behavior
 *
 * Provides picker activation, value persistence, and keyboard shortcuts
 * for datetime entry in DataTable cells.
 * Uses DateInputUtils and DateUtils for ISO 8601 datetime conversion.
 *
 * ## Features
 *
 * - **Picker activation**: Double-click or programmatic activation opens native datetime-local picker
 * - **Keyboard shortcuts**:
 *   - `Enter`: Save and navigate down (or up with Shift)
 *   - `Escape`: Cancel without saving
 * - **Workflow-aware navigation**: Respects workflow exit columns, preventing auto-navigation
 * - **Event cleanup**: Uses AbortController for proper listener cleanup
 *
 * ## Usage with Auto-Insert
 *
 * This hook provides picker control. For auto-insert behavior (Enter on empty cell),
 * use `useAutoInsertTimestamp` hook in combination with this one.
 *
 * @param config - Configuration object with coordinate and display format
 * @returns Object containing cell state, picker control, and mouse handlers
 *
 * @example
 * // Timestamp cell with seconds precision
 * const { cell, picker, mouseHandlers } = usePickerCellBehavior({
 *   coordinate,
 *   positionOffset,
 *   displayFormat: PickerDisplayFormat.DateTimeHoursMinutesSeconds,
 * });
 *
 * @example
 * // Collect cell with minute precision
 * const { cell, picker, mouseHandlers } = usePickerCellBehavior({
 *   coordinate,
 *   positionOffset,
 *   displayFormat: PickerDisplayFormat.DateTimeHoursMinutes,
 * });
 */
export const usePickerCellBehavior = (config: UsePickerCellBehaviorConfig): UsePickerCellBehaviorReturn => {
  const { coordinate, positionOffset, displayFormat } = config;
  const { dataTableService } = useDataTable();
  const pickerInputRef = useRef<HTMLInputElement>(null);
  const [active, setActive] = useState(false);
  const [cellValueOverride, setCellValueOverride] = useState<string | null>(null);

  const handleAbort = useCallback(() => {
    setActive(false);
    setCellValueOverride(null);
  }, []);

  const { getSignal, abort } = useGlobalEventListenerAbort({ onAbort: handleAbort });

  const { idCoordinate, cellRef } = useCellCoordinate(coordinate);
  const { cellData } = useCellData(idCoordinate, cellRef);
  const { cellState, tooltip } = useCellState(idCoordinate, cellRef);
  const { selectionState } = useCellSelectionState(coordinate, idCoordinate);
  const { containerStyle } = useCellPositioning(positionOffset, active);

  const persistInputValue = useCallback(
    (moveAfterPersistDirection?: DataTableDirection) => {
      setActive(false);
      setCellValueOverride(null);
      abort('completed');

      if (_isNil(idCoordinate) || _isNil(pickerInputRef.current)) {
        return;
      }

      const persistValue = DateInputUtils.localDateTimeToISODateTime(pickerInputRef.current.value);
      const shouldClearValue = persistValue === '' && _notNil(cellData?.value);
      const shouldUpdateValue = persistValue !== '' && persistValue !== cellData?.value;

      if (shouldClearValue) {
        dataTableService
          .updateCells([{ column_id: idCoordinate.column, row_id: idCoordinate.row, value: null }])
          .then(() => dataTableService.validate());
      } else if (shouldUpdateValue) {
        dataTableService
          .updateCells([{ column_id: idCoordinate.column, row_id: idCoordinate.row, value: persistValue }])
          .then(() => dataTableService.validate());
      }

      const shouldNavigate =
        _notNil(moveAfterPersistDirection) && !dataTableService.workflowService.workflowActionExit(idCoordinate.column);

      if (shouldNavigate) {
        dataTableService.moveSelection(moveAfterPersistDirection);
      }
    },
    [abort, cellData?.value, dataTableService, idCoordinate]
  );

  const activatePicker = useCallback(() => {
    if (dataTableService.readonly || _isNil(pickerInputRef.current)) {
      return;
    }

    const isoValue =
      DateUtils.isValidDateTime(cellData?.value) && _notNil(cellData?.value) ? cellData.value : DateUtils.dateTimeNow();

    setActive(true);
    const includeSeconds = displayFormat === PickerDisplayFormat.DateTimeHoursMinutesSeconds;
    pickerInputRef.current.value = DateInputUtils.toLocalDateTime(isoValue, { includeSeconds });

    const signal = getSignal();
    const eventOptions = { once: true, capture: true, signal };

    pickerInputRef.current.addEventListener('click', () => persistInputValue(), eventOptions);
    pickerInputRef.current.addEventListener('blur', () => persistInputValue(), eventOptions);
    pickerInputRef.current.addEventListener(
      'keyup',
      (event) => {
        if (event.code === 'Enter') {
          persistInputValue(event.shiftKey ? 'UP' : 'DOWN');
        } else if (event.code === 'Escape') {
          persistInputValue();
        }
      },
      eventOptions
    );

    window.requestAnimationFrame(() => {
      pickerInputRef?.current?.showPicker();
      pickerInputRef?.current?.focus();
    });
  }, [cellData?.value, dataTableService.readonly, getSignal, persistInputValue, displayFormat]);

  // Mouse interaction handlers
  const { handleMouseDown, handleMouseEnter, handleMouseUp } = useCellMouseHandlers(coordinate);

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    const value = DateInputUtils.localDateTimeToISODateTime(event?.target?.value ?? '');
    setCellValueOverride(value === '' ? null : value);
  }, []);

  const cellValue = cellValueOverride ?? cellData?.value;
  const hasGroupBorder = dataTableService.rightBorderType(coordinate.column) === 'group-separator';

  return {
    cell: {
      value: cellValue,
      state: cellState,
      selectionState,
      hasGroupBorder,
      tooltip,
    },
    picker: {
      active,
      inputRef: pickerInputRef,
      positionStyle: containerStyle,
      handleInputChange,
      activate: activatePicker,
    },
    mouseHandlers: {
      onMouseDown: handleMouseDown,
      onMouseEnter: handleMouseEnter,
      onMouseUp: handleMouseUp,
    },
  };
};
